// @ts-strict-ignore
import React, { ChangeEvent, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { Box as ApprovalRangeRow, Box, Combobox } from '@node-space/ui-components'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useAccountPreferencesQuery } from 'hooks/queries/useAccountPreferencesQuery'
import ApprovalFromToRange from './ApprovalFromToRange'
import ApprovalRangeControls from './ApprovalRangeControls'
import { ApprovalBandDraft, useApproverCount } from './hooks/useApprovalRangeSettings'

interface ApprovalRangeFormProps {
  approvalBands: ApprovalBandDraft[]
  setApprovalBands: (value: SetStateAction<ApprovalBandDraft[]>) => void
  isApprovalRuleInvalid: boolean
  setIsApprovalRuleInvalid: (value: SetStateAction<boolean>) => void
}

const ApprovalRangeForm = ({
  approvalBands,
  setApprovalBands,
  isApprovalRuleInvalid,
  setIsApprovalRuleInvalid,
}: ApprovalRangeFormProps) => {
  const { t } = useTranslation()

  const {
    profile: { currencyCode },
  } = useProfileContext()
  const { data: accountPreferences, isLoading: isLoadingAccountPreferences } =
    useAccountPreferencesQuery()
  const baseCurrency =
    (!isLoadingAccountPreferences && (accountPreferences?.currency || currencyCode)) || ''

  const approversCount = (index: number) => {
    const isFirstAndNotOnlyRow = index === 0 && approvalBands?.length > 1
    return isFirstAndNotOnlyRow ? useApproverCount(0) : useApproverCount(1)
  }

  const handleFormChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const newFieldsData = [...approvalBands]
    if (newFieldsData?.[index + 1] && event?.target?.value) {
      newFieldsData[index + 1].from = Number(event?.target?.value)
    }
    setApprovalBands(newFieldsData)
  }

  return (
    <>
      {approvalBands?.map((approvalBand, index) => (
        <ApprovalRangeRow key={index} gapX={8} className="grid grid-cols-5">
          <Box className="col-span-3">
            <ApprovalFromToRange
              rowIndex={index}
              handleInputChange={handleFormChange}
              rangeValues={approvalBand}
              approvalBands={approvalBands}
              postLabel={baseCurrency}
              isApprovalRuleInvalid={isApprovalRuleInvalid}
              setIsApprovalRuleInvalid={setIsApprovalRuleInvalid}
            />
          </Box>
          <Box>
            <Combobox
              items={approversCount(index)}
              id={t('approvals.approvers')}
              label={t('approvals.approvers')}
              defaultValue={
                index === 0 &&
                approvalBands?.length === 1 &&
                approvalBands?.[index]?.approvals === 0
                  ? '1'
                  : `${approvalBands?.[index]?.approvals}`
              }
              onChange={value => {
                approvalBands[index].approvals = Number(value)
                handleFormChange.call(handleFormChange)
              }}
            />
          </Box>
          <Box flex align="center" paddingT={24} gapX={8}>
            <ApprovalRangeControls
              rowIndex={index}
              approvalBands={approvalBands}
              setApprovalBands={setApprovalBands}
              setIsApprovalRuleInvalid={setIsApprovalRuleInvalid}
            />
          </Box>
        </ApprovalRangeRow>
      ))}
    </>
  )
}

export default ApprovalRangeForm
